import React, { useEffect, useState, useRef } from 'react'
import io from 'socket.io-client'
import '../css/ExplorePage.css'
import { useNavigate } from 'react-router-dom';

const SOCKET_URL = '/';
const KEEP_MESSAGES = 10;

function ChatPage() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [currentConv, setCurrentConv] = useState(null);
  const currentConvRef = useRef(null);
  const [mutualsList, setMutualsList] = useState([]);
  const [mutualsQuery, setMutualsQuery] = useState('');
  // use lazy-loading page size (5 per load) and fetch more as user scrolls
  const mutualsLimitRef = useRef(5);
  const mutualsSkipRef = useRef(0);
  const mutualsLoadingRef = useRef(false);
  const mutualsHasMoreRef = useRef(true);
  const [mutualsLoadingInitial, setMutualsLoadingInitial] = useState(true);
  const [mutualsLoadingMore, setMutualsLoadingMore] = useState(false);
  const mutualsListRef = useRef(null);
  const mutualsDebounceRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [unreadMap, setUnreadMap] = useState({});
  const socketRef = useRef();
  const convClearHandlerRef = useRef(null);
  const newMessageHandlerRef = useRef(null);
  const convDeletedHandlerRef = useRef(null);

  const convListRef = useRef(null);
  const limitRef = useRef(10);
  const skipRef = useRef(0);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showNewConv, setShowNewConv] = useState(false);

  // fetch a page of conversations (pagination for lazy loading)
  function fetchConversationsPage(reset = false) {
    if (!token) return;
    if (!reset && (loadingRef.current || !hasMoreRef.current)) return;
    if (reset) { skipRef.current = 0; hasMoreRef.current = true; setConversations([]); }
    loadingRef.current = true;
    if (reset) { setLoadingInitial(true); } else { setLoadingMore(true); }
    const url = `/conversations?limit=${limitRef.current}&skip=${skipRef.current}`;
    fetch(url, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(data => {
        const list = data || [];
        setConversations(prev => {
          if (reset) return list;
          const existingIds = new Set(prev.map(p => p._id));
          const merged = [...prev];
          for (const c of list) {
            if (!existingIds.has(c._id)) merged.push(c);
          }
          return merged;
        });
        const count = Array.isArray(list) ? list.length : 0;
        skipRef.current += count;
        if (count < limitRef.current) { hasMoreRef.current = false; }
      })
      .catch(() => {})
      .finally(() => { loadingRef.current = false; setLoadingInitial(false); setLoadingMore(false); });
  }

  const messagesEndRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const token = localStorage.getItem('jwt');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  useEffect(() => {
    if (!token) return;
    // initial paginated load
    fetchConversationsPage(true);

    // fetch first page of mutual-follow users (paginated)
    fetchMutualsPage(true);

    // setup or reuse global socket
    // reuse global socket
    socketRef.current = window.__socket;
    if (!socketRef.current) return; // socket not yet initialized

    if (user) {
      socketRef.current.emit('setup', user._id);
    }

    // set up newMessage handler for THIS chat page instance (appending to UI)
    const handleNewMessage = (msg) => {
      const active = currentConvRef.current;
      if (active && msg && msg.conversation && msg.conversation.toString() === active._id.toString()) {
        setMessages(prev => {
          if (prev.some(m => m._id === msg._id)) return prev;
          const arr = [...prev, msg];
          return arr.length > KEEP_MESSAGES ? arr.slice(arr.length - KEEP_MESSAGES) : arr;
        });
        const token = localStorage.getItem('jwt');
        fetch('/conversations/' + active._id + '/read', { method: 'POST', headers: { Authorization: 'Bearer ' + token } }).catch(()=>{});
        setUnreadMap(prev => ({ ...prev, [active._id]: 0 }));
      } else if (msg && msg.conversation) {
        // message for a different conversation, refresh paginated list
        fetchConversationsPage(true);
      }
    };
    newMessageHandlerRef.current = handleNewMessage;
    socketRef.current.on('newMessage', handleNewMessage);

    // set up conversationCleared handler
    const handleConversationCleared = ({ conversationId }) => {
      if (currentConvRef.current && currentConvRef.current._id === conversationId) {
        setMessages([]);
      }
    };
    convClearHandlerRef.current = handleConversationCleared;
    socketRef.current.on('conversationCleared', handleConversationCleared);

    // set up conversationDeleted handler (when unfollowed, conversation and messages deleted)
    const handleConversationDeleted = ({ conversationIds }) => {
      // remove deleted conversations from the list
      setConversations(prev => prev.filter(c => !conversationIds.includes(c._id)));
      // if currently viewing a deleted conversation, close it
      if (currentConvRef.current && conversationIds.includes(currentConvRef.current._id)) {
        setCurrentConv(null);
        currentConvRef.current = null;
        setMessages([]);
        alert('This conversation was deleted because one user unfollowed the other.');
      }
    };
    convDeletedHandlerRef.current = handleConversationDeleted;
    socketRef.current.on('conversationDeleted', handleConversationDeleted);

    return () => {
      // properly remove all handlers when ChatPage unmounts
      if (socketRef.current) {
        if (newMessageHandlerRef.current) {
          socketRef.current.off('newMessage', newMessageHandlerRef.current);
        }
        if (convClearHandlerRef.current) {
          socketRef.current.off('conversationCleared', convClearHandlerRef.current);
        }
        if (convDeletedHandlerRef.current) {
          socketRef.current.off('conversationDeleted', convDeletedHandlerRef.current);
        }
      }
      // clear active conversation when ChatPage is unmounted
      try { window.__activeConversation = null; } catch (e) {}
    }
  }, []);

  // refresh mutuals when follow relationships change elsewhere
  useEffect(() => {
    const refreshMutuals = () => {
      if (!token) return;
      fetchMutualsPage(true);
    };
    const handler = (e) => refreshMutuals();
    window.addEventListener('follow-changed', handler);
    return () => window.removeEventListener('follow-changed', handler);
  }, []);

  // fetch a page of mutual-follow users (for Start Conversation modal)
  function fetchMutualsPage(reset = false) {
    if (!token) return;
    if (!reset && (mutualsLoadingRef.current || !mutualsHasMoreRef.current)) return;
    if (reset) { mutualsSkipRef.current = 0; mutualsHasMoreRef.current = true; setMutualsList([]); }
    mutualsLoadingRef.current = true;
    if (reset) { setMutualsLoadingInitial(true); } else { setMutualsLoadingMore(true); }
    const qParam = mutualsQuery ? `&q=${encodeURIComponent(mutualsQuery)}` : '';
    const url = `/mutuals?limit=${mutualsLimitRef.current}&skip=${mutualsSkipRef.current}${qParam}`;
    fetch(url, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
        .then(list => {
          const arr = list || [];
          setMutualsList(prev => {
            if (reset) {
              // on reset replace list and set skip to returned count
              mutualsSkipRef.current = arr.length;
              if (arr.length < mutualsLimitRef.current) mutualsHasMoreRef.current = false;
              return arr;
            }
            // append unique items only
            const existing = new Set(prev.map(u => u._id));
            const filtered = arr.filter(u => !existing.has(u._id));
            mutualsSkipRef.current += filtered.length;
            if (filtered.length < mutualsLimitRef.current) mutualsHasMoreRef.current = false;
            return [...prev, ...filtered];
          });
        })
      .catch(() => {})
      .finally(() => { mutualsLoadingRef.current = false; setMutualsLoadingInitial(false); setMutualsLoadingMore(false); });
  }

  // when modal opens, ensure we have a page loaded (show spinner until it arrives)
  useEffect(() => {
    if (showNewConv) {
      if (mutualsList.length === 0 && !mutualsLoadingRef.current) {
        fetchMutualsPage(true);
      }
    }
  }, [showNewConv]);

  // debounce search input changes
  useEffect(() => {
    if (mutualsDebounceRef.current) clearTimeout(mutualsDebounceRef.current);
    mutualsDebounceRef.current = setTimeout(() => {
      // reset pagination and fetch new query
      mutualsSkipRef.current = 0;
      mutualsHasMoreRef.current = true;
      fetchMutualsPage(true);
    }, 300);
    return () => { if (mutualsDebounceRef.current) clearTimeout(mutualsDebounceRef.current); };
  }, [mutualsQuery]);

  // fetch unread counts per conversation
  const refreshUnreadMap = () => {
    if (!token) return;
    fetch('/conversations/unread', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(list => {
        const map = {};
        (list || []).forEach(item => { map[item._id] = item.count; });
        setUnreadMap(map);
      }).catch(()=>setUnreadMap({}));
  }

  useEffect(()=>{
    refreshUnreadMap();
    const handler = ()=> refreshUnreadMap();
    window.addEventListener('unread-updated', handler);
    return ()=> window.removeEventListener('unread-updated', handler);
  },[]);

  // attach scroll listener to conversations list for lazy loading more
  useEffect(() => {
    const el = convListRef.current;
    if (!el) return;
    const onScroll = () => {
      if (loadingRef.current || !hasMoreRef.current) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
        fetchConversationsPage();
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // auto-load more if the conversations container doesn't fill the view but there are more
  useEffect(() => {
    const el = convListRef.current;
    if (!el) return;
    if (loadingRef.current || !hasMoreRef.current) return;
    if (el.scrollHeight <= el.clientHeight) {
      fetchConversationsPage();
    }
  }, [conversations.length]);

  // attach scroll listener to mutuals modal list for lazy loading mutual-follow users
  useEffect(() => {
    const el = mutualsListRef.current;
    if (!showNewConv || !el) return;
    const onScroll = () => {
      if (mutualsLoadingRef.current || !mutualsHasMoreRef.current) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
        fetchMutualsPage();
      }
    };
    el.addEventListener('scroll', onScroll);
    // auto-load if container doesn't fill
    if (!mutualsLoadingRef.current && mutualsHasMoreRef.current && el.scrollHeight <= el.clientHeight) {
      fetchMutualsPage();
    }
    return () => el.removeEventListener('scroll', onScroll);
  }, [showNewConv, mutualsList.length]);

  useEffect(() => {
    if (!currentConv || !token) return;
    fetch('/messages/' + currentConv._id, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(data => {
        const arr = data || [];
        setMessages(arr.length > KEEP_MESSAGES ? arr.slice(arr.length - KEEP_MESSAGES) : arr);
        // scroll to bottom after messages load
        setTimeout(scrollToBottom, 0);
      });
  }, [currentConv]);

  useEffect(() => {
    // scroll to bottom whenever messages change
    scrollToBottom();
  }, [messages]);

  const openConversation = (conv) => {
    setCurrentConv(conv);
    currentConvRef.current = conv;
    try { window.__activeConversation = conv._id; } catch (e) {}
    // mark messages read for this conversation
    const token = localStorage.getItem('jwt');
    fetch('/conversations/' + conv._id + '/read', { method: 'POST', headers: { Authorization: 'Bearer ' + token } })
      .then(()=>{
        // refresh unread map and notify others
        refreshUnreadMap();
        window.dispatchEvent(new CustomEvent('unread-updated', { detail: { conversationId: conv._id } }));
      }).catch(()=>{});
  }

  const sendMessage = () => {
    if (!newMsg.trim() || !currentConv) return;
    const body = { conversationId: currentConv._id, text: newMsg };
    // do not emit via socket here; POST /messages will persist and server will broadcast
    // persist via REST
    fetch('/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify(body)
    }).then(r => r.json()).then(saved => {
      setMessages(prev => {
        if (!saved || !saved._id) return prev;
        const exists = prev.some(m => m._id === saved._id);
        const arr = exists ? prev : [...prev, saved];
        return arr.length > KEEP_MESSAGES ? arr.slice(arr.length - KEEP_MESSAGES) : arr;
      });
      setNewMsg('');
      // refresh conversations ordering
      fetchConversationsPage(true);
    }).catch(err => console.error(err));
  }

  const startChatWith = (otherUserId) => {
    // create or open conversation
    fetch('/conversations/' + otherUserId, { method: 'POST', headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(conv => {
        if (conv && conv._id) {
          setConversations(prev => [conv, ...prev.filter(c => c._id !== conv._id)]);
          setCurrentConv(conv);
        }
      }).catch(err => {
        // likely mutual-follow error
        alert(err && err.message ? err.message : 'Unable to start chat');
      });
  }

  const deleteConversation = (convId) => {
    if (!window.confirm('Delete this conversation and its messages? This cannot be undone.')) return;
    fetch('/conversations/' + convId, { method: 'DELETE', headers: { Authorization: 'Bearer ' + token } })
      .then(async r => {
        if (!r.ok) {
          const j = await r.json().catch(()=>({}));
          throw new Error(j && j.error ? j.error : 'Unable to delete conversation');
        }
        return r.json();
      })
      .then(() => {
        setConversations(prev => prev.filter(c => c._id !== convId));
        if (currentConvRef.current && currentConvRef.current._id === convId) {
          setCurrentConv(null);
          currentConvRef.current = null;
          setMessages([]);
        }
      })
      .catch(err => alert(err && err.message ? err.message : 'Delete failed'));
  }

  return (
    <div style={{height:'85vh',display:'flex',flexDirection:'column',background:'var(--surface)'}}>
      {!currentConv ? (
        <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
          <div style={{padding:'15px 20px',borderBottom:'1px solid var(--border)',background:'transparent', display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <h2 style={{margin:0,fontSize:'32px',fontWeight:800,color:'var(--text)'}}>Messages</h2>
            <div>
              <button onClick={() => setShowNewConv(true)} title="New conversation" style={{background:'#0084ff',color:'#fff',border:'none',borderRadius:8,padding:'8px 10px',cursor:'pointer',fontWeight:700}}>New</button>
            </div>
          </div>
          <div ref={convListRef} style={{flex:1,overflowY:'auto'}}>
        {/* Conversations List View */}
            {loadingInitial ? (
              <div style={{padding:40,display:'flex',justifyContent:'center'}}>
                <div className="spinner" aria-hidden="true"></div>
              </div>
            ) : (
              conversations.map(conv => {
              const other = conv.participants.find(p => p._id !== user._id);
              const unread = unreadMap[conv._id] || 0;
                  return (
                <div key={conv._id} onClick={() => openConversation(conv)} style={{padding:'12px 15px',cursor:'pointer',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{flex:1}}>
                    <strong style={{fontSize:'15px',color:'var(--text)'}}>{other ? other.name : 'Unknown'}</strong>
                    <div style={{fontSize:'13px',color:'var(--muted)'}}>@{other ? other.userName : ''}</div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    {unread > 0 && <div style={{background:'#0084ff',color:'#fff',borderRadius:12,padding:'6px 10px',fontSize:12,fontWeight:'bold',marginLeft:'10px'}}>{unread}</div>}
                    <button onClick={(e) => { e.stopPropagation(); deleteConversation(conv._id); }} title="Delete conversation" style={{background:'transparent',border:'none',color:'var(--danger)',cursor:'pointer',fontSize:16}}>🗑</button>
                  </div>
                </div>
              )
              })
            )}
            {loadingMore && (
              <div style={{padding:'12px 15px',display:'flex',justifyContent:'center'}}>
                <div className="spinner" aria-hidden="true"></div>
              </div>
            )}
            {conversations.length === 0 && mutualsList.length > 0 && (
              <div style={{padding:'20px'}}>
                <h4 style={{margin:'0 0 15px 0'}}>No chats yet. Start a new chat!</h4>
                {mutualsList.map(u => (
                  <div key={u._id} style={{padding:'12px 15px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div style={{flex:1}}>
                      <strong style={{fontSize:'15px',color:'var(--text)'}}>{u.name}</strong>
                      <div style={{fontSize:'13px',color:'var(--muted)'}}>@{u.userName}</div>
                    </div>
                    <button onClick={() => startChatWith(u._id)} style={{padding:'8px 16px',background:'#0084ff',color:'#fff',border:'none',borderRadius:'20px',cursor:'pointer',fontWeight:'bold',fontSize:'14px'}}>Message</button>
                  </div>
                ))}
              </div>
            )}
            {conversations.length === 0 && mutualsList.length === 0 && (
              <div style={{padding:'40px 20px',textAlign:'center',color:'var(--muted)'}}>
                <p style={{fontSize:'15px'}}>No conversations yet. Follow someone to start chatting!</p>
              </div>
            )}
            {/* New conversation modal */}
            {showNewConv && (
              <div style={{position:'fixed',left:0,top:0,right:0,bottom:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:3000}} onClick={() => setShowNewConv(false)}>
                <div onClick={e => e.stopPropagation()} style={{background:'var(--surface)',width:'90%',maxWidth:420,borderRadius:8,padding:16,maxHeight:'70vh',display:'flex',flexDirection:'column',color:'var(--text)'}}>
                  <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:6}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <h4 style={{margin:0,color:'var(--text)'}}>Start Conversation</h4>
                      <button onClick={() => setShowNewConv(false)} style={{background:'transparent',border:'none',fontSize:20,cursor:'pointer',color:'var(--text)'}}>×</button>
                    </div>
                    <div>
                      <input
                        type="text"
                        value={mutualsQuery}
                        onChange={e => setMutualsQuery(e.target.value)}
                        placeholder="Search by username"
                        style={{width:'100%',padding:'8px 10px',border:'1px solid rgba(234,246,238,0.03)',borderRadius:6,fontSize:14,background:'transparent',color:'var(--text)'}}
                      />
                    </div>
                  </div>
                  <div ref={mutualsListRef} style={{overflowY:'auto',flex:1,paddingRight:6}}>
                    {mutualsLoadingInitial ? (
                      <div style={{padding:24,display:'flex',justifyContent:'center'}}>
                        <div className="spinner" aria-hidden="true"></div>
                      </div>
                    ) : mutualsList && mutualsList.length > 0 ? (
                      mutualsList.map(u => (
                        <div key={u._id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border)'}}>
                          <div style={{display:'flex',alignItems:'center',gap:12}}>
                            <img src={u.Photo || 'https://cdn-icons-png.flaticon.com/128/1177/1177568.png'} alt="" style={{width:42,height:42,borderRadius:21,objectFit:'cover'}} />
                            <div>
                              <div style={{fontWeight:700,color:'var(--text)'}}>{u.name}</div>
                              <div style={{fontSize:12,color:'var(--muted)'}}>@{u.userName}</div>
                            </div>
                          </div>
                          <div>
                            <button onClick={() => { startChatWith(u._id); setShowNewConv(false); }} style={{background:'#0084ff',color:'#fff',border:'none',padding:'8px 12px',borderRadius:6,cursor:'pointer'}}>Message</button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{padding:12,color:'var(--muted)'}}>No mutual follows available to message.</div>
                    )}
                    {mutualsLoadingMore && (
                      <div style={{padding:12,display:'flex',justifyContent:'center'}}>
                        <div className="spinner" aria-hidden="true"></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
          <div style={{padding:'12px 15px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',position:'sticky', backgroundColor:"#280202", top:0,zIndex:30}}>
            <button onClick={() => {setCurrentConv(null); currentConvRef.current = null; setMessages([]);}} style={{background:'none',border:'none',fontSize:'24px',cursor:'pointer',marginRight:'12px',padding:0}}>
              ←
            </button>
            <div>
              <div style={{fontSize:'15px',fontWeight:600}}>
                <span style={{color:'var(--text)'}}>{currentConv && currentConv.participants.find(p => p._id !== user._id)?.name}</span>
              </div>
            </div>
            <button style={{backgroundColor:"red", color:"white", borderRadius:"20px", border:"none", marginLeft:"10px"}} onClick={() => navigate('/profile/home-post-options')} >Options</button>
          </div>

          <div style={{backgroundColor: "black", flex:1,overflowY:'auto',padding:'10px 15px'}}>
            {messages.map(m => (
              <div key={m._id} style={{margin:'8px 0',display:'flex',flexDirection:m.sender._id === user._id ? 'row-reverse' : 'row',alignItems:'flex-end'}}>
                <div style={{maxWidth:'70%',background:m.sender._id === user._id ? '#0084ff' : 'rgba(234,246,238,0.02)',color:m.sender._id === user._id ? '#fff' : 'var(--text)',padding:'8px 12px',borderRadius:'18px'}}>
                  <div style={{fontSize:'13px',color:m.sender._id === user._id ? 'rgba(255,255,255,0.7)' : 'var(--muted)'}}>{m.sender.name}</div>
                  <div style={{fontSize:'15px',marginTop:'2px'}}>{m.text}</div>
                  <div style={{fontSize:'11px',color:m.sender._id === user._id ? 'rgba(255,255,255,0.6)' : 'var(--muted)',textAlign:'right',marginTop:'2px'}}>{new Date(m.createdAt).toLocaleTimeString()}</div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

            <div style={{padding:'12px 15px',borderTop:'1px solid var(--border)',display:'flex',alignItems:'center',gap:'10px',background:'black'}}>
              <input
                type="text"
                value={newMsg}
                onChange={e => setNewMsg(e.target.value.slice(0, 300))}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={currentConv ? 'Aa' : 'Select a conversation'}
                style={{ flex: 1, padding: '10px 16px', border: '1px solid rgba(234,246,238,0.03)', borderRadius: '20px', outline: 'none', fontSize: '15px', background:'transparent', color:'var(--text)' }}
                disabled={!currentConv}
                maxLength={300}
            />
            <button onClick={sendMessage} disabled={!currentConv || !newMsg.trim()} style={{background:newMsg.trim() && currentConv ? '#0084ff' : 'rgba(255,255,255,0.06)',color:'#fff',border:'none',borderRadius:'50%',width:'36px',height:'36px',cursor: newMsg.trim() && currentConv ? 'pointer' : 'default',fontSize:'18px',display:'flex',alignItems:'center',justifyContent:'center'}}>
              ➤
            </button>
            {currentConv && (
              <button onClick={() => {
                  if (window.confirm('Delete all messages in this conversation?')) {
                    fetch('/conversations/' + currentConv._id + '/clear', { method: 'DELETE', headers: { Authorization: 'Bearer ' + token } })
                      .then(() => {
                        setMessages([]);
                        refreshUnreadMap();
                        window.dispatchEvent(new CustomEvent('unread-updated', { detail: { conversationId: currentConv._id } }));
                      });
                  }
                }} style={{background:'#ff4458',color:'#fff',border:'none',borderRadius:'20px',padding:'8px 14px',cursor:'pointer',fontSize:'12px',fontWeight:'bold'}}>Clear</button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ChatPage
